import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { isNativeApp } from 'common/webview';
import { isLoggedIn } from 'common/utils/auth';
import i18n from 'i18next';
import { IcSmile } from 'components/common/icons/EmojiIcons';
import {
    ActionList, ActionRow, AppScreen, Button, ButtonFooter, EmptyState,
    InfoGrid, InfoItem, Section,
} from 'components/ds';
import styles from './InvitationEntry.module.css';

/**
 * 초대 링크 진입 페이지
 * - 앱 WebView: 로그인 상태면 바로 참여 → 모임 상세 이동
 * - 브라우저: 모임 정보 확인 → 참여 방법 선택
 */
export const InvitationEntry = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { inviteCode: rawInviteCode } = useParams();
    // 초대코드는 항상 대문자로 정규화 (URL에서 소문자로 들어올 수 있음)
    const inviteCode = rawInviteCode?.toUpperCase();
    const [meeting, setMeeting] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isJoining, setIsJoining] = useState(false);

    const inApp = isNativeApp();

    // 브라우저에서만 앱 딥링크 시도
    useEffect(() => {
        if (!inviteCode || inApp) return;

        const userAgent = navigator.userAgent.toLowerCase();
        const isKakao = userAgent.includes('kakaotalk');

        if (isKakao) {
            // 카카오 인앱브라우저: intent scheme으로 앱 실행 후 브라우저 닫기
            const intentUrl = `intent://invitation/${inviteCode}#Intent;scheme=dokocrew;package=com.dokoiot.dokocrew;end`;
            window.location.href = intentUrl;
            return;
        }

        // 일반 브라우저: iframe 딥링크 시도 (앱이 없어도 에러 없음)
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = `dokocrew://invitation/${inviteCode}`;
        document.body.appendChild(iframe);

        const timer = setTimeout(() => {
            document.body.removeChild(iframe);
        }, 2000);

        return () => {
            clearTimeout(timer);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };
    }, [inviteCode, inApp]);

    // 모임 정보 조회
    useEffect(() => {
        if (!inviteCode) return;

        const fetchMeeting = async () => {
            try {
                const response = await MeetingRepository.onGetMeetingByInviteCode(inviteCode);
                // axios onRequest 는 4xx/5xx 를 throw 하지 않고 response 를 그대로 반환한다.
                // status 검사 없이 진행하면 400 에러 바디({code,message,...})가 truthy 라
                // 유효 모임으로 오인되어 모임명 "모임" 가짜 카드가 렌더된다.
                const status = response?.status;
                if (typeof status === 'number' && (status < 200 || status >= 300)) {
                    setError(response?.data?.message || t('invitation.invalidInviteCode'));
                    setIsLoading(false);
                    return;
                }
                const meetingInfo = response?.data?.data || response?.data;
                if (!meetingInfo || !meetingInfo.meetingId) {
                    setError(t('invitation.invalidInviteCode'));
                    setIsLoading(false);
                    return;
                }

                // 이미 참여자인 경우 → 모임 상세로 즉시 이동 (참가 화면 스킵)
                if (meetingInfo.isAlreadyMember && meetingInfo.meetingId) {
                    navigate(`/meeting/detail/${meetingInfo.meetingId}`, { replace: true });
                    return;
                }

                setMeeting(meetingInfo);
            } catch (e: any) {
                console.error('초대 정보 조회 실패:', e);
                setError(t('invitation.loadFailed'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchMeeting();
    }, [inviteCode, navigate, t]);

    // 앱 안 + 로그인 상태: 바로 참여 시도
    const handleAutoJoin = async () => {
        if (!inviteCode || isJoining) return;
        setIsJoining(true);
        try {
            const response = await MeetingRepository.onPostJoinMeetingByCode(inviteCode);
            if (response?.status === 200 || response?.status === 201) {
                // 신규 참여 또는 이미 참여 중 → meetingId 반환됨
                const meetingId = response?.data?.data || response?.data?.meetingId;
                if (meetingId) {
                    // 초대 [참여] 클릭 = 참석 의사 명확 → ATTENDING 사전 설정
                    // 백엔드 join 은 default UNDECIDED 라 별도 PUT 으로 갱신 (모임상세에서 재차 prompt 안 뜨도록)
                    try {
                        await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: 'ATTENDING' });
                    } catch (e) {
                        console.warn('초대 참여 후 ATTENDING 사전 설정 실패 — 모임상세에서 다시 묻습니다', e);
                    }
                    navigate(`/meeting/detail/${meetingId}`, { replace: true, state: { meetingName: meeting?.meetingName } });
                    return;
                }
            }
        } catch (e: any) {
            console.error('자동 참여 실패:', e);
        }
        setIsJoining(false);
    };

    const handleJoin = () => {
        // 회원(앱/브라우저 무관)이면 즉시 가입 — 4단계 비회원 선택 화면을 거치지 않음
        if (isLoggedIn()) {
            handleAutoJoin();
            return;
        }
        // 기존 게스트 세션이 있으면 바로 모임 상세로 이동
        const guestToken = sessionStorage.getItem('guestToken');
        const guestMeetingId = sessionStorage.getItem('guestMeetingId');
        if (guestToken && guestMeetingId && meeting?.meetingId === guestMeetingId) {
            navigate(`/meeting/detail/${guestMeetingId}`, { replace: true });
            return;
        }
        navigate(`/invitation/info/${inviteCode}`, {
            state: { meeting }
        });
    };

    const handleDecline = () => {
        if (inApp) {
            navigate('/', { replace: true });
        } else {
            window.close();
            navigate('/');
        }
    };

    if (isLoading) {
        return (
            <AppScreen bar="none">
                <div className={styles.center}>
                    <p className={styles.loadingText}>{t('invitation.loadingMeeting')}</p>
                </div>
            </AppScreen>
        );
    }

    if (error) {
        return (
            <AppScreen bar="none">
                <EmptyState
                    icon={<IcSmile size={44} />}
                    title={error}
                    action={inApp ? (
                        <Button variant="secondary" onClick={() => navigate('/', { replace: true })}>
                            {t('invitation.goBackHome')}
                        </Button>
                    ) : undefined}
                />
            </AppScreen>
        );
    }

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString(i18n.language, {
            year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
        });
    };

    /* 모임 정보 — 값이 있는 것만. 기존 화면의 6가지를 그대로 옮겼습니다. */
    const infoItems: InfoItem[] = [];
    if (meeting?.creatorNickname) {
        infoItems.push({
            label: t('invitation.labelHost'),
            value: (() => {
                try { return decodeURIComponent(meeting.creatorNickname); } catch { return meeting.creatorNickname; }
            })(),
        });
    }
    if (meeting?.meetingType) {
        infoItems.push({
            label: t('invitation.labelType'),
            value: meeting.meetingType === 'TRAVEL'
                ? t('invitation.travelMeeting')
                : t('invitation.dayMeeting'),
        });
    }
    if (meeting?.travelDestination) {
        infoItems.push({ label: t('invitation.labelDest'), value: meeting.travelDestination });
    }
    if (meeting?.travelStartDate && meeting?.travelEndDate) {
        infoItems.push({
            label: t('invitation.labelPeriod'),
            value: `${formatDate(meeting.travelStartDate)} ~ ${formatDate(meeting.travelEndDate)}`,
            full: true,
        });
    }
    if (meeting?.currentMemberCount > 0) {
        infoItems.push({
            label: t('invitation.labelMembers'),
            value: t('invitation.membersParticipating', { count: meeting.currentMemberCount }),
        });
    }

    return (
        <AppScreen bar="none" hasFooter>
            <div className={styles.intro}>
                <span className={styles.eyebrow}>{t('invitation.invited')}</span>
                <h1 className={styles.name}>
                    {meeting?.meetingName || t('invitation.defaultMeetingName')}
                </h1>
                {meeting?.description ? (
                    <p className={styles.desc}>{meeting.description}</p>
                ) : null}
            </div>

            {infoItems.length > 0 && (
                <Section spacing="tight">
                    <InfoGrid items={infoItems} />
                </Section>
            )}

            {/* 이미 비회원으로 참여한 적이 있으면 그 쪽으로 */}
            {meeting?.meetingId && (
                <Section spacing="tight">
                    <ActionList>
                        <ActionRow
                            title={t('invitation.alreadyJoinedGuest')}
                            onClick={() => navigate(`/guest-login/${meeting.meetingId}`)}
                        />
                    </ActionList>
                </Section>
            )}

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={handleJoin} disabled={isJoining}>
                    {isJoining ? t('invitation.joining') : t('invitation.join')}
                </Button>
                <Button variant="ghost" size="lg" block onClick={handleDecline}>
                    {inApp ? t('invitation.goHome') : t('invitation.decline')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
